<template>
  <div class="page-container spot-kline-repair">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item><span class="page-title">系统设置</span></el-breadcrumb-item>
      <el-breadcrumb-item>现货K线管理</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="page-content" shadow="never">
      <div slot="header">查询现货1m K线</div>
      <el-alert
        title="仅支持至少5分钟前、且当前仍保存在 MongoDB 中的1m K线。修复会影响所有使用该交易对的租户。"
        type="warning"
        :closable="false"
        show-icon
      />
      <el-form ref="searchForm" :inline="true" :model="search" :rules="searchRules" class="search-form">
        <el-form-item label="交易对" prop="symbolId">
          <el-select v-model="search.symbolId" filterable placeholder="请选择交易对" @change="clearKline">
            <el-option
              v-for="item in symbolList"
              :key="item.id"
              :label="item.symbol"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="周期">
          <el-input value="1m" disabled />
        </el-form-item>
        <el-form-item label="时间" prop="time">
          <el-date-picker
            v-model="search.time"
            type="datetime"
            format="yyyy-MM-dd HH:mm"
            value-format="timestamp"
            :picker-options="pickerOptions"
            placeholder="请选择1m K线开始时间"
            @change="handleTimeChange"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="querying" @click="queryKline">查询</el-button>
          <el-button @click="reset">重置</el-button>
        </el-form-item>
      </el-form>

      <el-divider />

      <el-form ref="repairForm" :model="form" :rules="repairRules" label-width="90px" class="repair-form">
        <el-row :gutter="24">
          <el-col :xs="24" :md="12">
            <el-form-item label="交易对"><el-input :value="form.symbol" disabled /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="周期"><el-input value="1m" disabled /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="开盘价" prop="open"><el-input v-model.trim="form.open" /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="收盘价" prop="close"><el-input v-model.trim="form.close" /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="最高价" prop="high"><el-input v-model.trim="form.high" /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="最低价" prop="low"><el-input v-model.trim="form.low" /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="成交量" prop="amount"><el-input v-model.trim="form.amount" /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="成交额" prop="volume"><el-input v-model.trim="form.volume" /></el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="修复原因" prop="reason">
              <el-input v-model="form.reason" type="textarea" :rows="2" maxlength="200" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item>
              <el-button type="success" :loading="submitting" :disabled="!klineLoaded || polling" @click="submitRepair">
                修复K线
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <el-card v-if="task.repairId" class="task-card" shadow="never">
        <div slot="header">修复任务</div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="任务ID">{{ task.repairId }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="statusType">{{ task.status }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="交易对">{{ task.symbol }}</el-descriptions-item>
          <el-descriptions-item label="说明">{{ task.message }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <el-form :inline="true" class="task-query">
        <el-form-item label="任务ID">
          <el-input v-model.trim="repairIdInput" placeholder="输入任务ID重新查询" style="width: 360px" />
        </el-form-item>
        <el-form-item>
          <el-button :loading="statusQuerying" @click="queryStatusById">查询状态</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import api from "./api";
import Decimal from "decimal.js";

const EMPTY_FORM = () => ({
  symbolId: "",
  symbol: "",
  time: "",
  open: "",
  close: "",
  high: "",
  low: "",
  amount: "",
  volume: "",
  reason: "",
});

const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

export default {
  name: "SpotKlineRepair",
  data() {
    const positiveDecimal = (rule, value, callback) => {
      if (!value) return callback(new Error("请输入数值"));
      if (!DECIMAL_PATTERN.test(value) || /^0+(\.0+)?$/.test(value)) return callback(new Error("请输入大于0的数值"));
      callback();
    };
    const nonNegativeDecimal = (rule, value, callback) => {
      if (value === "" || value === null || value === undefined) return callback(new Error("请输入数值"));
      if (!DECIMAL_PATTERN.test(value)) return callback(new Error("请输入不小于0的数值"));
      callback();
    };
    return {
      symbolList: [],
      search: { symbolId: "", time: "" },
      form: EMPTY_FORM(),
      task: {},
      repairIdInput: "",
      querying: false,
      submitting: false,
      polling: false,
      statusQuerying: false,
      klineLoaded: false,
      pollTimer: null,
      pollCount: 0,
      pickerOptions: {
        disabledDate(date) {
          return date.getTime() > Date.now() - 5 * 60 * 1000;
        },
      },
      searchRules: {
        symbolId: [{ required: true, message: "请选择交易对", trigger: "change" }],
        time: [{ required: true, message: "请选择时间", trigger: "change" }],
      },
      repairRules: {
        open: [{ validator: positiveDecimal, trigger: "blur" }],
        close: [{ validator: positiveDecimal, trigger: "blur" }],
        high: [{ validator: positiveDecimal, trigger: "blur" }],
        low: [{ validator: positiveDecimal, trigger: "blur" }],
        amount: [{ validator: nonNegativeDecimal, trigger: "blur" }],
        volume: [{ validator: nonNegativeDecimal, trigger: "blur" }],
        reason: [
          { required: true, message: "请输入修复原因", trigger: "blur" },
          { max: 200, message: "修复原因最多200字符", trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    statusType() {
      return this.task.status === "SUCCESS" ? "success" : this.task.status === "FAILED" ? "danger" : "warning";
    },
  },
  created() {
    this.loadSymbols();
    if (this.$route.query.symbolId) this.search.symbolId = Number(this.$route.query.symbolId);
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    loadSymbols() {
      api.getSymbols().then(res => {
        const data = res.data || {};
        this.symbolList = data.list || [];
      });
    },
    handleTimeChange(value) {
      if (value) this.search.time = Math.floor(value / 60000) * 60000;
      this.clearKline();
    },
    validateHistoryTime() {
      return this.search.time && this.search.time % 60000 === 0 && this.search.time <= Date.now() - 5 * 60 * 1000;
    },
    clearKline() {
      this.klineLoaded = false;
      const reason = this.form.reason;
      this.form = { ...EMPTY_FORM(), reason };
    },
    queryKline() {
      this.$refs.searchForm.validate(valid => {
        if (!valid) return;
        if (!this.validateHistoryTime()) {
          this.$message.error("只能选择至少5分钟前、按分钟对齐的时间");
          return;
        }
        this.querying = true;
        api.getKline(this.search).then(res => {
          const data = res.data;
          this.form = Object.keys(EMPTY_FORM()).reduce((result, key) => {
            result[key] = data[key] === null || data[key] === undefined ? "" : String(data[key]);
            return result;
          }, {});
          this.form.symbolId = data.symbolId;
          this.form.time = data.time;
          this.klineLoaded = true;
          this.$nextTick(() => this.$refs.repairForm.clearValidate());
        }).finally(() => {
          this.querying = false;
        });
      });
    },
    submitRepair() {
      this.$refs.repairForm.validate(valid => {
        if (!valid) return;
        const high = new Decimal(this.form.high);
        const low = new Decimal(this.form.low);
        const open = new Decimal(this.form.open);
        const close = new Decimal(this.form.close);
        if (high.lessThan(Decimal.max(open, close, low)) || low.greaterThan(Decimal.min(open, close))) {
          this.$message.error("请检查最高价和最低价关系");
          return;
        }
        this.$confirm(
          "修复后将自动重算全部周期，并影响所有使用该交易对的租户，是否继续？",
          "确认修复",
          { type: "warning", confirmButtonText: "确认修复" }
        ).then(() => {
          this.submitting = true;
          const payload = { ...this.form, reason: this.form.reason.trim() };
          delete payload.symbol;
          return api.repairKline(payload).then(res => {
            this.task = res.data;
            this.repairIdInput = res.data.repairId;
            this.$message.success("修复任务已提交，正在查询处理状态");
            this.startPolling();
          }).finally(() => {
            this.submitting = false;
          });
        }).catch(() => {});
      });
    },
    startPolling() {
      this.stopPolling();
      this.polling = true;
      this.pollCount = 0;
      this.pollTimer = setInterval(this.pollStatus, 1000);
    },
    pollStatus() {
      this.pollCount += 1;
      api.getRepairStatus(this.task.repairId).then(res => {
        this.task = res.data;
        if (this.task.status === "SUCCESS") {
          this.stopPolling();
          this.$message.success(this.task.message || "K线修复成功");
          this.queryKline();
        } else if (this.task.status === "FAILED") {
          this.stopPolling();
          this.$message.error(this.task.message || "K线修复失败");
        } else if (this.pollCount >= 60) {
          this.stopPolling();
          this.$message.warning(`任务仍在处理中，请稍后凭任务ID查询：${this.task.repairId}`);
        }
      }).catch(() => {
        this.stopPolling();
      });
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.polling = false;
    },
    queryStatusById() {
      if (!this.repairIdInput) {
        this.$message.warning("请输入任务ID");
        return;
      }
      this.statusQuerying = true;
      api.getRepairStatus(this.repairIdInput).then(res => {
        this.task = res.data;
        if (this.task.status === "PROCESSING") this.startPolling();
      }).finally(() => {
        this.statusQuerying = false;
      });
    },
    reset() {
      this.stopPolling();
      this.search = { symbolId: "", time: "" };
      this.form = EMPTY_FORM();
      this.task = {};
      this.repairIdInput = "";
      this.klineLoaded = false;
      this.$nextTick(() => {
        this.$refs.searchForm.clearValidate();
        this.$refs.repairForm.clearValidate();
      });
    },
  },
};
</script>

<style lang="less" scoped>
.search-form { margin-top: 22px; }
.repair-form { width: 100%; max-width: 960px; }
.task-card { margin-top: 24px; }
.task-query { margin-top: 20px; }
</style>
